import type { RouteObject } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { ProjectsList } from './screens/ProjectsList';
import { ProjectDetail } from './screens/ProjectDetail';
import { CourseDetail } from './screens/CourseDetail';
import { CoursesTab } from './screens/tabs/CoursesTab';
import { VoiceProfilesTab } from './screens/tabs/VoiceProfilesTab';
import { ArtStylesTab } from './screens/tabs/ArtStylesTab';
import { GenerationsTab } from './screens/tabs/GenerationsTab';
import { DraftsTab } from './screens/tabs/DraftsTab';
import { CollaboratorsTab } from './screens/tabs/CollaboratorsTab';
import { DraftDetail } from './screens/DraftDetail';
import { ApiKeys } from './screens/ApiKeys';

// Central, modular route table for the authenticated app.
//
// The tree is intentionally data-driven (an array of `RouteObject`) so later
// page tasks extend it without rewriting the router. `ProjectDetail` renders a
// tab nav + <Outlet />; each tab below is a nested child route:
//
//   - Cursos (index)   → CoursesTab      — stub replaced by tasks 16.4 / 16.5
//   - Perfiles de voz  → VoiceProfilesTab — stub replaced by task 16.6
//   - Estilos visuales → ArtStylesTab     — stub replaced by task 16.6
//   - Generaciones     → GenerationsTab    — stub replaced by task 16.7
//   - Colaboradores    → CollaboratorsTab  — implemented in task 16.3
//
// Later tasks only swap the stub component bodies (and may add sibling routes
// such as "projects/:projectId/courses/:courseId" for course detail in 16.4);
// the tab route structure itself stays put.
//
// Everything renders inside `AppLayout` (shared header/nav) via the parent
// route's <Outlet />. Keep additions declarative here rather than branching in
// App.tsx.
export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      // Projects list — the app landing screen (Req 2.3, 2.4).
      { index: true, element: <ProjectsList /> },
      // API-keys management (signed-in Cognito users only).
      { path: 'api-keys', element: <ApiKeys /> },
      // Project detail — tabbed shell; each tab is a nested route below
      // (Req 3.1, 3.2, 4.1, 10.1, 10.3).
      {
        path: 'projects/:projectId',
        element: <ProjectDetail />,
        children: [
          { index: true, element: <CoursesTab /> },
          { path: 'voice-profiles', element: <VoiceProfilesTab /> },
          { path: 'art-styles', element: <ArtStylesTab /> },
          { path: 'generations', element: <GenerationsTab /> },
          { path: 'drafts', element: <DraftsTab /> },
          { path: 'collaborators', element: <CollaboratorsTab /> },
        ],
      },
      // Course detail — a full-screen sibling of the project tabs (it renders
      // its own back link rather than living inside the tab surface). Shows the
      // course + ordered episodes with per-episode media generation and
      // presigned-URL playback (Req 4.3, 7.1, 8.1, 9.2) — task 16.4.
      {
        path: 'projects/:projectId/courses/:courseId',
        element: <CourseDetail />,
      },
      // Draft detail — a full-screen sibling of the project tabs. Shows one AI
      // course generation's current state (proposing / awaiting review /
      // generating / failed), the review+approve action when a proposal is
      // ready, and per-episode generation progress once approved.
      {
        path: 'projects/:projectId/drafts/:generationRequestId',
        element: <DraftDetail />,
      },
    ],
  },
];
